import { FC, useState, useEffect } from 'react';

import {
  convertCurrencyToNumber,
  DSOLineItem,
  DSOLineItemMultiShip,
  DsoShoppingCartProps,
  Styled,
} from './';
import { Button, Checkbox, IconButton } from 'components/core';
import { useRouter } from 'next/router';
import {
  useDsoEmptyCart,
  useDsoProceedToCheckout,
  useDsoSwitchToSingleShip,
} from 'reactQuery/mutations';
import Image from 'next/legacy/image';
import { LoadingSpinner } from 'styles/sharedStyles';
import { useIsMutating } from 'react-query';
import { useI18n } from 'contexts/i18n';
import { DSOConfirmModal } from 'components/DSO/DSOConfirmModal';

const DSOShoppingCart: FC<DsoShoppingCartProps> = ({
  dsoCart,
  setDsoProceedToCheckout,
}) => {
  const router = useRouter();
  const i18n = useI18n();

  const isMutating = useIsMutating();
  const [shipToMultipleAddresses, setShipToMultipleAddresses] = useState(
    dsoCart.isMultiShipOrder
  );
  const [showSingleShipConfirm, setShowSingleShipConfirm] = useState(false);
  const emptyCartMutation = useDsoEmptyCart();

  const emptyCart = () => {
    emptyCartMutation.mutate();
  };
  const [checkoutError, setCheckoutError] = useState<string[]>([]);
  const [highlightEmptyAddresses, setHighlightEmptyAddresses] = useState(false);
  const dsoProceedToCheckoutMutation = useDsoProceedToCheckout(
    setDsoProceedToCheckout,
    setCheckoutError
  );
  const dsoSwitchToSingleShipMutation = useDsoSwitchToSingleShip(
    setShipToMultipleAddresses
  );

  const clearCheckoutValidationError = () => {
    setCheckoutError([]);
    setHighlightEmptyAddresses(false);
  };

  const hasMultipleShipAddresses = () => {
    const accountNumbers = new Set<string>();
    for (const lineItem of dsoCart.lineItems) {
      for (const child of lineItem.children) {
        const acct = child.shipAccountNumber?.trim();
        if (acct) accountNumbers.add(acct);
        if (accountNumbers.size > 0) return true;
      }
    }
    return false;
  };

  const handleShipToggle = () => {
    if (shipToMultipleAddresses) {
      if (hasMultipleShipAddresses()) {
        setShowSingleShipConfirm(true);
      } else {
        setShipToMultipleAddresses(false);
        clearCheckoutValidationError();
      }
    } else {
      setShipToMultipleAddresses(true);
    }
  };

  const confirmSwitchToSingleShip = () => {
    clearCheckoutValidationError();
    setShowSingleShipConfirm(false);
    dsoSwitchToSingleShipMutation.mutate();
  };

  const hasUnselectedAddresses = () => {
    return dsoCart.lineItems.some(lineItem =>
      lineItem.children.some(child => !child.shipAccountNumber?.trim())
    );
  };

  const isHavingDiscount =
    convertCurrencyToNumber(dsoCart.displayOrderSavingsTotal) > 0; // displayOrderSavingsTotal returns positive (eg. "$10.00") even when it's a discount, so we check if it's greater than 0
  useEffect(() => {
    if (!hasUnselectedAddresses()) {
      clearCheckoutValidationError();
    }
  }, [dsoCart.lineItems]);

  const redirectToCheckout = () => {
    if (shipToMultipleAddresses && hasUnselectedAddresses()) {
      setCheckoutError([
        'Please select a shipping address for all items before proceeding to checkout.',
      ]);
      setHighlightEmptyAddresses(true);
      return;
    }
    setCheckoutError([]);
    setHighlightEmptyAddresses(false);
    dsoProceedToCheckoutMutation.mutate();
  };

  return (
    <>
      <Styled.ShoppingCartHead data-testid="shopping-cart-head">
        {dsoCart.lineItems.length > 0 && (
          <Styled.ShippingOptionWrapper
            data-testid="shipping-option-wrapper"
            $isChecked={shipToMultipleAddresses}
            $disabled={isMutating > 0}
          >
            <Styled.ShippingOptionCheckbox>
              <Checkbox
                label=""
                checked={shipToMultipleAddresses}
                onChange={handleShipToggle}
                disabled={isMutating > 0}
              />
              <Styled.ShippingOptionContent onClick={handleShipToggle}>
                <Styled.ShippingOptionLabel>
                  Ship to multiple addresses?
                </Styled.ShippingOptionLabel>
                <Styled.ShippingOptionHint>
                  Perfect for sending to multiple locations
                </Styled.ShippingOptionHint>
              </Styled.ShippingOptionContent>
            </Styled.ShippingOptionCheckbox>
          </Styled.ShippingOptionWrapper>
        )}
        {dsoCart.lineItems.length > 0 && (
          <Styled.CartHeaderButtonsContainer>
            <IconButton
              data-testid="add-more-button"
              width={'150px'}
              size="md"
              onClick={() => router.push('/dso')}
              showBorder={true}
            >
              <>
                <span style={{ fontSize: '1.4rem', fontWeight: '300' }}>+</span>
                <Styled.EmptyCartButtonLabel>
                  Add more
                </Styled.EmptyCartButtonLabel>
              </>
            </IconButton>
            <IconButton
              data-testid="empty-cart-button"
              width={'150px'}
              size="md"
              onClick={emptyCart}
              showBorder={true}
            >
              <>
                {!emptyCartMutation.isLoading && (
                  <Image
                    src="/images/icons/delete.svg"
                    alt="me"
                    width="24"
                    height="24"
                  />
                )}
                {emptyCartMutation.isLoading && (
                  <LoadingSpinner width="20px" height="20px" />
                )}
                <Styled.EmptyCartButtonLabel>
                  {i18n['Empty Cart']}
                </Styled.EmptyCartButtonLabel>
              </>
            </IconButton>
          </Styled.CartHeaderButtonsContainer>
        )}
      </Styled.ShoppingCartHead>

      {dsoCart.lineItems.length === 0 && (
        <Styled.EmptyCartMessage data-testid="empty-cart-message">
          <p>
            {i18n['Your cart is currently empty!'] ||
              'Your cart is currently empty '}
          </p>
          <Button
            testId="return-to-shop-button"
            size="xl"
            onClick={() => {
              router.push('/dso');
            }}
          >
            {i18n['Return To Shop']}
          </Button>
        </Styled.EmptyCartMessage>
      )}
      {dsoCart.lineItems.length > 0 && (
        <Styled.ShoppingCartContainer data-testid="shopping-cart-container">
          <Styled.ShoppingCartSection>
            {!shipToMultipleAddresses && (
              <Styled.LineItemHeader $isMultiShip={shipToMultipleAddresses}>
                <Styled.LineItemHeaderTitle>
                  {i18n['Product']}
                </Styled.LineItemHeaderTitle>
                <Styled.LineItemHeaderTitle></Styled.LineItemHeaderTitle>
                <Styled.LineItemHeaderTitle>
                  {i18n['Quantity']}
                </Styled.LineItemHeaderTitle>
                <Styled.LineItemHeaderTitle>
                  {i18n['Unit Price']}
                </Styled.LineItemHeaderTitle>
                <Styled.LineItemHeaderTitle>
                  {i18n['Total']}
                </Styled.LineItemHeaderTitle>
              </Styled.LineItemHeader>
            )}
            {shipToMultipleAddresses
              ? dsoCart.lineItems.map(lineItem => {
                  return (
                    <DSOLineItemMultiShip
                      key={lineItem.lineItemId}
                      lineItem={lineItem}
                      highlightEmptyAddresses={highlightEmptyAddresses}
                      onAddressChange={clearCheckoutValidationError}
                      setCheckoutError={setCheckoutError}
                    />
                  );
                })
              : dsoCart.lineItems.map(lineItem => {
                  return (
                    <DSOLineItem
                      key={lineItem.lineItemId}
                      lineItem={lineItem}
                      setCheckoutError={setCheckoutError}
                    />
                  );
                })}
          </Styled.ShoppingCartSection>

          <Styled.CheckoutSection>
            <Styled.SubTotalTitle data-testid="subtotal-title">
              {`${i18n['Subtotal']}(${i18n['items:']} ${dsoCart.lineItems.length})`}
            </Styled.SubTotalTitle>
            {isMutating > 0 ? (
              <Styled.LoadingSpinnerWrapper data-testid="checkout-loading">
                <LoadingSpinner />
              </Styled.LoadingSpinnerWrapper>
            ) : (
              <Styled.SubTotalPriceContainer>
                <Styled.SubTotalWithDiscount data-testid="subtotal-amount">
                  {dsoCart.displayOrderNetTotal ??
                    dsoCart.displayOrderGrandTotal}
                </Styled.SubTotalWithDiscount>
                {isHavingDiscount && (
                  <Styled.TotalSavings data-testid="total-savings">
                    {i18n['Total Savings']}: {dsoCart.displayOrderSavingsTotal}
                  </Styled.TotalSavings>
                )}
              </Styled.SubTotalPriceContainer>
            )}

            <Button
              testId="checkout-button"
              variant="primary"
              fullWidth={true}
              onClick={redirectToCheckout}
              disabled={
                dsoProceedToCheckoutMutation.isLoading ||
                isMutating > 0 ||
                checkoutError.length > 0
              }
            >
              {i18n['Checkout']}
            </Button>
            {checkoutError?.length > 0 && (
              <Styled.CheckoutError data-testid="checkout-error">
                <Styled.CheckoutErrorHeading>
                  Errors
                </Styled.CheckoutErrorHeading>
                <Styled.CheckoutErrorList>
                  {checkoutError.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </Styled.CheckoutErrorList>
              </Styled.CheckoutError>
            )}
          </Styled.CheckoutSection>
        </Styled.ShoppingCartContainer>
      )}

      <DSOConfirmModal
        isOpen={showSingleShipConfirm}
        onClose={() => setShowSingleShipConfirm(false)}
        onConfirm={confirmSwitchToSingleShip}
        title={
          i18n[
            'Are you sure you would like to ship all the items to a single address?'
          ]
        }
        description={
          i18n[
            'You will lose all the addresses that have been added under multiple shipping option. In case you decide to change your mind, you can always reselect the addresses again.'
          ]
        }
      />
    </>
  );
};

export default DSOShoppingCart;
